import React, { SetStateAction, useEffect, useRef, useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import DocumentTab from "./DocumentTab";
import WebsiteTab from "./WebsiteTab";
import CsvTab from "./CsvTab";
import JsonTab from "./JsonTab";
import CombinedTab from "./CombinedTab";
import SharePointTab from "./SharePointTab"; // Add this import
import {
  KnowledgeBase,
  KnowledgeBaseCombine,
  knowledgeDoc,
  KnowledgeWebsite,
} from "../../../Shared-ui/Types/KnowledgeBase";
import { use } from "marked";
import { KnowledgeBaseTypeEnum } from "../../../Shared-ui/Enum/KnowledgeBaseEnum";

interface MyComponentProps {
  setKnowledgeBase: React.Dispatch<SetStateAction<KnowledgeBase>>;
  knowledgeBase: KnowledgeBase;
  knowledgeBaseDoc: knowledgeDoc;
  setKnowledgeBaseDoc: React.Dispatch<SetStateAction<knowledgeDoc>>;
  setKnowledgeBasWebsite: React.Dispatch<SetStateAction<KnowledgeWebsite>>;
  setKnowledgeBaseCombine: React.Dispatch<SetStateAction<KnowledgeBaseCombine>>;
  knowledgeBaseCombine: KnowledgeBaseCombine;
  setKnowledgeBaseSharePoint: React.Dispatch<SetStateAction<any>>;
  knowledgeBaseSharePoint: any; // Add this line
  // Add new prop for the API call
  onSharePointCreated?: (toolId: string, kbId: string) => void;
}

const KnowledgeBaseTabs: React.FC<MyComponentProps> = (props) => {
  const knowledgeBaseType: Record<number, string> = {
    0: KnowledgeBaseTypeEnum.PDF,
    1: KnowledgeBaseTypeEnum.WEBSITE,
    2: KnowledgeBaseTypeEnum.COMBINED,
    3: KnowledgeBaseTypeEnum.SHAREPOINT, // Add SharePoint enum
  };
  const [activeTab, setActiveTab] = useState(0);
  const currentTab = useRef(activeTab);
  const [tabDisable, setTabDisable] = useState({
    document: false,
    website: false,
    combined: false,
    sharepoint: false, // Add SharePoint disable state
  });


  useEffect(() => {
    if (props.knowledgeBase.id) {
      const documentTypes = ["pdf", "doc", "docx", "json", "csv", "ppt", "pptx"];
      setTabDisable((prev) => ({
        ...prev,
        document: documentTypes.includes(props.knowledgeBase.type) ? false : true,
        website: props.knowledgeBase.type === KnowledgeBaseTypeEnum.WEBSITE ? false : true,
        combined: props.knowledgeBase.type === KnowledgeBaseTypeEnum.COMBINED ? false : true,
        sharepoint: props.knowledgeBase.type === KnowledgeBaseTypeEnum.SHAREPOINT ? false : true,
      }));

      let tabId = 0;
      if (documentTypes.includes(props.knowledgeBase.type)) {
        tabId = 0;
      } else {
        tabId = Number(
          Object.keys(knowledgeBaseType).find(
            (key) => knowledgeBaseType[Number(key)] === props.knowledgeBase.type
          )
        );
      }
      setActiveTab(tabId);
    }
  }, [props.knowledgeBase.id, props.knowledgeBase.type]);





  const handleSwitchTab = (tabIndex: number) => {
    setActiveTab(tabIndex);
    props.setKnowledgeBase((preVal) => {
      return {
        ...preVal,
        type: knowledgeBaseType[tabIndex]
      }
    });
  };

  // Add function to handle SharePoint file retrieval
  const handleSharePointFileRetrieval = async (toolId: string, kbId: string) => {
    try {
      const formData = new FormData();
      formData.append('kb_id', kbId);
      formData.append('description', '');
      formData.append('process_immediately', 'false');
      formData.append('auto_process_all', 'false');

      // Use dynamic values from knowledgeBaseSharePoint state
      const queryParams = new URLSearchParams({
        site: props.knowledgeBaseSharePoint?.site,
        server_relative_urls: props.knowledgeBaseSharePoint?.files?.[0]?.server_relative_url
      });

      const response = await fetch(`/api/sharepoint/${toolId}/retrieve-files?${queryParams}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`, // Adjust based on your auth token storage
          'accept': 'application/json',
        },
        body: formData
      });

      if (response.ok) {
        console.log('SharePoint files retrieved successfully');
        // Handle success response
      } else {
        console.error('Failed to retrieve SharePoint files');
        // Handle error response
      }
    } catch (error) {
      console.error('Error retrieving SharePoint files:', error);
    }
  };


  return (
    <Box>
      <Box>
        <Typography variant="body1" fontWeight={500}>
          Source
        </Typography>
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
          value={activeTab}
          onChange={(e) => handleSwitchTab(Number(e.target.value))}
        >
          <FormControlLabel
            value="0"
            control={<Radio />}
            label="Document"
            disabled={tabDisable.document}
          />
          <FormControlLabel
            value="1"
            control={<Radio />}
            label="Website"
            disabled={tabDisable.website}
          />
          <FormControlLabel
            value="2"
            control={<Radio />}
            label="Combined"
            disabled={tabDisable.combined}
          />
          <FormControlLabel
            value="3"
            control={<Radio />}
            label="SharePoint"
            disabled={tabDisable.sharepoint}
          />
        </RadioGroup>
        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && (
            <DocumentTab
              setKnowledgeBaseDoc={props.setKnowledgeBaseDoc}
              knowledgeBase={props.knowledgeBase}
              setKnowledgeBase={props.setKnowledgeBase}
            />
          )}
          {activeTab === 1 && (
            <WebsiteTab
              setKnowledgeBaseWebsite={props.setKnowledgeBasWebsite}
              knowledgeBase={props.knowledgeBase}
            />
          )}
          {activeTab === 2 && (
            <CombinedTab
              setKnowledgeBaseCombine={props.setKnowledgeBaseCombine}
              knowledgeBaseCombine={props.knowledgeBaseCombine}
              knowledgeBase={props.knowledgeBase}
              onSwitchToDocumentTab={handleSwitchTab}
            />
          )}
          {activeTab === 3 && (
            <SharePointTab
              knowledgeBase={props.knowledgeBase}
              setKnowledgeBase={props.setKnowledgeBase}
              setKnowledgeBaseOfSharePoint={props.setKnowledgeBaseSharePoint}
              onFileRetrieval={handleSharePointFileRetrieval}
            />
          )}
        </Box>
      </Box>

    </Box>
  );
};

export default KnowledgeBaseTabs;
